import type { FieldState } from 'final-form';
import { isEmail, filterFreeDomains, isPhoneNumber } from '@lucid/utils';
import isArray from 'lodash/isArray';

import type { Lucid } from '../../../types/types';

type ReturnValueType = string | undefined;
type ShouldCheckRequired = boolean | null;
type CustomErrorMessage = string | null;

type ComposeValidators = (
  value: Lucid.Marketo.FormFieldStateValues,
  allValues?: object,
  meta?: FieldState,
) => ReturnValueType;

/** This is a type guard to let TypeScript know if the option passed is an object. */
export const isFieldObject = (option: Lucid.Marketo.FormFieldStateValues): option is Lucid.Marketo.Values =>
  !!Object.keys(Object.assign({}, option) as Lucid.Marketo.Values).length;

export const composeValidators =
  (...validators: ComposeValidators[]) =>
  (value: Lucid.Marketo.FormFieldStateValues, allValues?: object, meta?: FieldState) =>
    validators.reduce<ReturnValueType>((error, validator) => error || validator(value, allValues, meta), undefined);

export const isRequired =
  (shouldCheckRequired?: ShouldCheckRequired, customErrorMessage?: CustomErrorMessage) =>
  (value: Lucid.Marketo.FormFieldStateValues): ReturnValueType => {
    if (shouldCheckRequired) {
      if (isArray(value)) {
        if (value.length) return undefined;

        return customErrorMessage || 'Required';
      }

      if (value) return undefined;

      return customErrorMessage || 'Required';
    }

    return undefined;
  };

export const isSelectRequired =
  (shouldCheckRequired?: ShouldCheckRequired, customErrorMessage?: CustomErrorMessage) =>
  (option: Lucid.Marketo.FormFieldStateValues): ReturnValueType => {
    if (shouldCheckRequired) {
      if (option) return undefined;

      return customErrorMessage || 'Required';
    }

    return undefined;
  };

export const isValidPhoneNumber =
  (type: string, customErrorMessage?: CustomErrorMessage) =>
  (value: Lucid.Marketo.FormFieldStateValues): ReturnValueType => {
    const isTelephoneType = type === 'tel';

    if (isTelephoneType) {
      if (value && isPhoneNumber.test(value as string)) return undefined;

      return customErrorMessage && /\d/.test(customErrorMessage)
        ? customErrorMessage
        : 'Must be a valid phone number. (e.g., 999-999-9999)';
    }

    return undefined;
  };

export const isValidEmail =
  (type: string, customErrorMessage?: CustomErrorMessage) =>
  (value: Lucid.Marketo.FormFieldStateValues): ReturnValueType => {
    const isEmailType = type === 'email';

    if (isEmailType) {
      if (isEmail.test(value as string)) return undefined;

      return customErrorMessage && customErrorMessage.includes('@')
        ? customErrorMessage
        : 'Must be a valid email. (e.g., name@example.com)';
    }

    return undefined;
  };

export const isValidEnterpriseEmail =
  (
    type: string,
    shouldRestrictFreeDomains: boolean,
    customErrorMessage?: CustomErrorMessage,
    handleEnterpriseError?: (message: string) => void,
  ) =>
  (value: Lucid.Marketo.FormFieldStateValues): ReturnValueType => {
    const isEmailType = type === 'email';

    if (isEmailType && shouldRestrictFreeDomains) {
      const errorMessage = customErrorMessage || 'Please enter a work email.';
      const { shouldRestrictDomain, currentDomain } = filterFreeDomains(value as string);

      if (shouldRestrictDomain) {
        const customEnterpriseErrorMessage = `Please enter a work email. Emails ending in ${currentDomain} are not allowed.`;
        if (handleEnterpriseError) handleEnterpriseError(customEnterpriseErrorMessage);

        return customEnterpriseErrorMessage || errorMessage;
      }

      return undefined;
    }

    return undefined;
  };
